import axios from "axios";
import { apiUrl, logApiError } from "@/shared/services/config.js";
import { ZIPCODE_NOT_AVAILABLE_MSG } from "@/shared/utils/misc.js";
import {
  QuoteFormData,
  QuotesRequest,
  RatingRequest,
  RatingResponseDTO,
  UpdateRatingRequest,
  ZipcodeDetailsRespose,
} from "@/shared/dtos/index.js";

const getApiErrorMessage = (error) => (
  error?.response?.data?.apierror?.message
  || error?.response?.data?.message
  || error?.message
);

/**
 * POST /auth/quotedata — fetch ILF/DLF defaults + limits for a (zip,
 * speciality, effective-date) tuple. Request is normalized through
 * QuotesRequest so missing fields are filled with safe defaults; the
 * response is wrapped in QuoteFormData so consumers always get an
 * instance with every field present.
 *
 * @param {Partial<QuotesRequest>} body
 * @returns {Promise<QuoteFormData>}
 */
export const postIlfDlf = async (body) => {
  try {
    const response = await axios.post(
      apiUrl("/auth/quotedata"),
      new QuotesRequest(body),
      { withCredentials: true },
    );
    return new QuoteFormData(response.data);
  } catch (error) {
    const message = getApiErrorMessage(error);
    if (String(message || "").toLowerCase().includes("zip code not found")) {
      throw new Error(ZIPCODE_NOT_AVAILABLE_MSG);
    }
    throw new Error(message || "Could not load coverage options for this zip. Please try again.");
  }
};

/**
 * GET /auth/{zip}/zipcodedata — resolve a ZIP to its city/state.
 * @returns {Promise<ZipcodeDetailsRespose>}
 */
export const getZipcodeData = async (zipcode) => {
  try {
    const response = await axios.get(
      apiUrl(`/auth/${zipcode}/zipcodedata`),
      { withCredentials: true },
    );
    return new ZipcodeDetailsRespose(response.data);
  } catch (error) {
    const message = getApiErrorMessage(error);
    throw new Error(message || "Could not validate this zip code. Please try again.");
  }
};

/**
 * POST /auth/calculate — compute a rating quote.
 * @param {Partial<RatingRequest>} body
 * @returns {Promise<RatingResponseDTO>}
 */
export const postCalculateRating = async (body) => {
  try {
    const response = await axios.post(
      apiUrl("/auth/calculate"),
      new RatingRequest(body),
      { withCredentials: true },
    );
    return new RatingResponseDTO(response.data);
  } catch (error) {
    logApiError(error);
    throw error;
  }
};

/**
 * PUT /insured/rating — update an existing submission's rating.
 * @param {Partial<UpdateRatingRequest>} body
 * @returns {Promise<RatingResponseDTO>}
 */
export const putUpdateRating = async (body) => {
  try {
    const response = await axios.put(
      apiUrl("/insured/rating"),
      new UpdateRatingRequest(body),
      { withCredentials: true },
    );
    return new RatingResponseDTO(response.data);
  } catch (error) {
    const message = getApiErrorMessage(error);
    throw new Error(message || "Could not update rating. Please try again.");
  }
};
